import {FormState} from '../../models/form-state.model';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {DictionaryActions} from '../dictionary/dictionary.actions';
import {DictionaryCode} from '../../enums/dictionary-code.enum';
import {clearFormValidationErrors, setFormStatus, setFormValidationErrors} from '../../helpers/form.helper';
import {RepositoryPoliciesAsideActions} from './repository-policies-aside.actions';
import {RepositoryPoliciesActions} from './repository-policies.actions';
import {RepositoryPoliciesService} from '../../services/repository-policies.service';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {throwError} from 'rxjs';
import {FormStatus} from '../../enums/file-status.enum';
import {ModalService} from '../../services/modal.service';
import {TranslateService} from '@ngx-translate/core';

export interface RepositoryPoliciesAsideStateModel {
    form: FormState;
    terminationReasons: string[];
    paybackAmount: number;
    documents: any[];
}

@State<RepositoryPoliciesAsideStateModel>({
    name: RepositoryPoliciesAsideState.STATE_NAME,
    defaults: {
        form: new FormState(),
        terminationReasons: [],
        paybackAmount: null,
        documents: null
    }
})
@Injectable()
export class RepositoryPoliciesAsideState {

    static readonly STATE_NAME = 'repositoryPoliciesAside';

    constructor(private repositoryPoliciesService: RepositoryPoliciesService,
                private modalService: ModalService,
                private translateService: TranslateService) {
    }

    @Selector()
    static formErrors(state: RepositoryPoliciesAsideStateModel) {
        return state.form.validationErrors;
    }

    @Selector()
    static terminationReasons(state: RepositoryPoliciesAsideStateModel) {
        return state.terminationReasons;
    }

    @Selector()
    static paybackAmount(state: RepositoryPoliciesAsideStateModel) {
        return state.paybackAmount;
    }

    @Selector()
    static documents(state: RepositoryPoliciesAsideStateModel) {
        return state.documents;
    }

    @Action(RepositoryPoliciesAsideActions.StartTermination)
    startTerminationAmendment(ctx: StateContext<RepositoryPoliciesAsideStateModel>) {
        ctx.dispatch(new DictionaryActions.Download([DictionaryCode.TERMINATION_REASON]))
            .subscribe(() => {
                ctx.dispatch(new RepositoryPoliciesActions.OpenTerminationAside());
            });
    }

    @Action(RepositoryPoliciesAsideActions.Termination)
    termination(ctx: StateContext<RepositoryPoliciesAsideStateModel>, action: RepositoryPoliciesAsideActions.Termination) {
        return this.repositoryPoliciesService.termination(action.policyId, action.data, true)
            .pipe(
                switchMap(() => ctx.dispatch(new RepositoryPoliciesActions.CloseAside())),
                switchMap(() => ctx.dispatch(new RepositoryPoliciesActions.GetRepositoryPolicyById(action.policyId))),
                catchError(error => {
                    ctx.dispatch(new RepositoryPoliciesAsideActions.FormFailure(error.error));
                    return throwError(error);
                }));
    }

    @Action(RepositoryPoliciesAsideActions.GetPaybackAmount)
    getPaybackAmount(ctx: StateContext<RepositoryPoliciesAsideStateModel>, action: RepositoryPoliciesAsideActions.GetPaybackAmount) {
        return this.repositoryPoliciesService.termination(action.policyId, action.data, false)
            .pipe(tap(policy => ctx.patchState({paybackAmount: policy.premiumToReturn})));
    }

    @Action(RepositoryPoliciesAsideActions.StartDataChange)
    startDataChange(ctx: StateContext<RepositoryPoliciesAsideStateModel>) {
        const dictionaries = [
            DictionaryCode.CLAUSES,
        ];
        ctx.dispatch(new DictionaryActions.Download(dictionaries))
            .subscribe(() => {
                ctx.dispatch(new RepositoryPoliciesActions.OpenDataChangeAside());
            });
    }

    @Action(RepositoryPoliciesAsideActions.DataChange)
    personalDataChange(ctx: StateContext<RepositoryPoliciesAsideStateModel>, action: RepositoryPoliciesAsideActions.DataChange) {
        return this.repositoryPoliciesService.dataChange(action.policyId, action.data)
            .pipe(
                switchMap(() => ctx.dispatch(new RepositoryPoliciesActions.CloseAside())),
                switchMap(() => ctx.dispatch(new RepositoryPoliciesActions.GetRepositoryPolicyById(action.policyId))),
                catchError(error => {
                    ctx.dispatch(new RepositoryPoliciesAsideActions.FormFailure(error.error));
                    return throwError(error);
                }));
    }

    @Action(RepositoryPoliciesAsideActions.GetDocuments)
    getDocuments(
        ctx: StateContext<RepositoryPoliciesAsideStateModel>,
        {policyId}: RepositoryPoliciesAsideActions.GetDocuments) {
        return this.repositoryPoliciesService.getDocuments(policyId)
            .pipe(
                tap(documents => ctx.patchState({documents})),
                map(() => ctx.dispatch(new RepositoryPoliciesActions.OpenDocumentsAside()))
            );
    }

    @Action(RepositoryPoliciesAsideActions.UpdateDocuments)
    updateDocuments(
        ctx: StateContext<RepositoryPoliciesAsideStateModel>,
        {policyId}: RepositoryPoliciesAsideActions.GetDocuments) {
        return this.repositoryPoliciesService.getDocuments(policyId)
            .pipe(
                tap(documents => ctx.patchState({documents})),
                tap(() => this.modalService.confirm({
                    nzCancelText: null,
                    nzOkText: this.translateService.instant('LABELS.CLOSE'),
                    nzContent: this.translateService.instant('REPOSITORY_POLICIES.ADD_DOCUMENT_SUCCESS'),
                }))
            );
    }

    @Action(RepositoryPoliciesAsideActions.GetTerminationReasons)
    getTerminationReasons(
        ctx: StateContext<RepositoryPoliciesAsideStateModel>,
        {policyId}: RepositoryPoliciesAsideActions.GetTerminationReasons) {
        return this.repositoryPoliciesService.getTerminationReasons(policyId)
            .pipe(tap(terminationReasons => ctx.patchState({terminationReasons})));
    }

    @Action(RepositoryPoliciesAsideActions.FormFailure)
    addFailure(ctx: StateContext<RepositoryPoliciesAsideStateModel>, {validationErrors = []}: RepositoryPoliciesActions.FormFailure) {
        setFormValidationErrors(ctx, validationErrors, RepositoryPoliciesAsideState.STATE_NAME);
    }

    @Action(RepositoryPoliciesAsideActions.AddDocument)
    addDocument(ctx: StateContext<RepositoryPoliciesAsideStateModel>, {policyId, file}: RepositoryPoliciesAsideActions.AddDocument) {
        setFormStatus(ctx, FormStatus.PENDING, RepositoryPoliciesAsideState.STATE_NAME);
        return this.repositoryPoliciesService.addDocument(policyId, file)
            .pipe(
                tap(() => {
                    ctx.patchState({form: new FormState()});
                    ctx.dispatch(new RepositoryPoliciesAsideActions.UpdateDocuments(policyId));
                }),
                catchError(({error}) => {
                    ctx.dispatch(new RepositoryPoliciesAsideActions.FormFailure(error));
                    return throwError(error);
                })
            );
    }

    @Action(RepositoryPoliciesAsideActions.DownloadDocument)
    downloadDocument(ctx: StateContext<RepositoryPoliciesAsideStateModel>,
                     {policyId, hash, fileName}: RepositoryPoliciesAsideActions.DownloadDocument) {
        return this.repositoryPoliciesService.getRepositoryTokenByHash(policyId, hash, fileName);
    }

    @Action(RepositoryPoliciesAsideActions.NoDataChange)
    noDataChange() {
        this.modalService.confirm({
            nzCancelText: null,
            nzTitle: this.translateService.instant('REPOSITORY_POLICIES.NO_DATA_CHANGE_TITLE'),
            nzOkText: this.translateService.instant('LABELS.CLOSE'),
            nzContent: this.translateService.instant('REPOSITORY_POLICIES.NO_DATA_CHANGE'),
        });
    }

    @Action(RepositoryPoliciesAsideActions.ClearValidationErrors)
    productClearValidationErrors(ctx: StateContext<RepositoryPoliciesAsideStateModel>) {
        clearFormValidationErrors(ctx);
    }
}
